import React, { Suspense, useDeferredValue, useEffect, useState } from 'react';
import { Company, Venue } from '@/types';
import {
  useCompanies,
  useCompanyMutations,
  DataErrorBoundary,
  type CompanyListParams
} from '@features/admin/data';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { Pagination } from '@shared/components/common/Pagination';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { formatUkDateTime } from '@/lib/dates';
import {
  Search,
  Plus,
  Edit2,
  Calendar,
  Users,
  Trash2,
  Loader2,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  MessageSquare,
  MapPin,
  CreditCard,
  FileText
} from 'lucide-react';

const getApprovalDetails = (company: Company) => {
  const logs = [...(company.changeLogs || [])].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  const approvalLog = logs.find((l) => l.field === 'Status' && l.newValue === 'active');
  const tierLog = logs.find((l) => l.field === 'Tier');
  const createdDate = formatUkDateTime(company.createdAt, '—');
  return {
    approvedBy: approvalLog ? approvalLog.actor : 'System/Import',
    approvedAt: approvalLog ? formatUkDateTime(approvalLog.timestamp, '—') : createdDate,
    tierSetBy: tierLog ? tierLog.actor : approvalLog ? approvalLog.actor : 'System/Import',
    tierSetAt: tierLog
      ? formatUkDateTime(tierLog.timestamp, '—')
      : approvalLog
        ? formatUkDateTime(approvalLog.timestamp, '—')
        : createdDate
  };
};

const getStatusChangeDetails = (company: Company) => {
  const logs = [...(company.changeLogs || [])].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  const statusLog = logs.find((l) => l.field === 'Status');
  if (statusLog) {
    return { actor: statusLog.actor, date: formatUkDateTime(statusLog.timestamp, '—') };
  }
  return { actor: 'System/Import', date: formatUkDateTime(company.createdAt, '—') };
};

interface CompanyListProps {
  venues: Venue[];
  canEdit: boolean;
  onViewActivity: (company: Company) => void;
  onViewTeam: (company: Company) => void;
  onEdit: (company: Company) => void;
}

export const CompanyList: React.FC<CompanyListProps> = ({
  venues,
  canEdit,
  onViewActivity,
  onViewTeam,
  onEdit
}) => {
  const companyMutations = useCompanyMutations();

  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [companySort, setCompanySort] = useState<{
    sortBy: 'createdAt' | 'name';
    sortDir: 'asc' | 'desc';
  }>({
    sortBy: 'createdAt',
    sortDir: 'desc'
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isDeletingCompanyId, setIsDeletingCompanyId] = useState<string | null>(null);

  // Reset to page 1 on search or sort change
  useEffect(() => {
    setPage(1);
  }, [search, companySort.sortBy, companySort.sortDir]);

  const toggleCompanyNameSort = () => {
    setCompanySort((prev) => {
      if (prev.sortBy !== 'name') return { sortBy: 'name', sortDir: 'asc' };
      if (prev.sortDir === 'asc') return { sortBy: 'name', sortDir: 'desc' };
      return { sortBy: 'createdAt', sortDir: 'desc' };
    });
  };

  const renderCompanyNameSortIcon = () => {
    if (companySort.sortBy !== 'name') {
      return <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />;
    }
    return companySort.sortDir === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 text-slate-600" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-slate-600" />
    );
  };

  const handleDeleteCompany = async (company: Company) => {
    if (!canEdit) return;
    const confirmed = await showConfirmModal(
      `Delete "${company.name}" and all of its related records (bookings, forms, certificates, payments, and team users)? This cannot be undone.`,
      { title: 'Delete Company', confirmText: 'Delete', cancelText: 'Cancel' }
    );
    if (!confirmed) return;
    setIsDeletingCompanyId(company.id);
    try {
      await companyMutations.remove(company.id);
    } catch (e: unknown) {
      showAlertModal(normalizeErrorMessage(e, 'Failed to delete company.'));
    } finally {
      setIsDeletingCompanyId(null);
    }
  };

  const newCompanyTemplate: Company = {
    id: '',
    name: '',
    contactPerson: '',
    email: '',
    address: '',
    status: 'active',
    tier: 'bronze',
    paymentPreference: 'invoice',
    requirePo: false,
    allowedVenueIds: venues.map((v) => v.id),
    createdAt: '',
    changeLogs: []
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-slate-800">Company Accounts</h3>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search companies..."
              className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none w-64 bg-white text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {canEdit && (
            <button
              onClick={() => onEdit(newCompanyTemplate)}
              className="bg-slate-900 text-white px-4 py-2 rounded-lg font-medium hover:bg-slate-800 flex items-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" /> Add Company
            </button>
          )}
        </div>
      </div>

      <DataErrorBoundary invalidateOnRetry="companies">
        <Suspense
          fallback={
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
              Loading companies...
            </div>
          }
        >
          <ActiveList
            page={page}
            pageSize={pageSize}
            search={deferredSearch}
            sortBy={companySort.sortBy}
            sortDir={companySort.sortDir}
            venues={venues}
            canEdit={canEdit}
            isDeletingCompanyId={isDeletingCompanyId}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            toggleCompanyNameSort={toggleCompanyNameSort}
            renderCompanyNameSortIcon={renderCompanyNameSortIcon}
            onViewActivity={onViewActivity}
            onViewTeam={onViewTeam}
            onEdit={onEdit}
            onDelete={handleDeleteCompany}
          />
        </Suspense>
      </DataErrorBoundary>
    </div>
  );
};

// Internal list component — not exported
interface ActiveListProps {
  page: number;
  pageSize: number;
  search: string;
  sortBy: 'createdAt' | 'name';
  sortDir: 'asc' | 'desc';
  venues: Venue[];
  canEdit: boolean;
  isDeletingCompanyId: string | null;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  toggleCompanyNameSort: () => void;
  renderCompanyNameSortIcon: () => React.ReactNode;
  onViewActivity: (company: Company) => void;
  onViewTeam: (company: Company) => void;
  onEdit: (company: Company) => void;
  onDelete: (company: Company) => void;
}

const ActiveList: React.FC<ActiveListProps> = ({
  page,
  pageSize,
  search,
  sortBy,
  sortDir,
  venues,
  canEdit,
  isDeletingCompanyId,
  onPageChange,
  onPageSizeChange,
  toggleCompanyNameSort,
  renderCompanyNameSortIcon,
  onViewActivity,
  onViewTeam,
  onEdit,
  onDelete
}) => {
  const params: CompanyListParams = {
    page,
    pageSize,
    q: search || undefined,
    status: 'active,on_hold,rejected',
    sortBy,
    sortDir
  };
  const result = useCompanies(params);
  const companies = result.items || [];
  const totalCount = Number(result.totalCount || 0);

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="p-4 font-medium text-slate-600 text-sm">
                <button
                  type="button"
                  onClick={toggleCompanyNameSort}
                  className="inline-flex items-center gap-1.5 hover:text-slate-900"
                  title="Toggle company name sort"
                >
                  Company Name
                  {renderCompanyNameSortIcon()}
                </button>
              </th>
              <th className="p-4 font-medium text-slate-600 text-sm">Contact</th>
              <th className="p-4 font-medium text-slate-600 text-sm">Address</th>
              <th className="p-4 font-medium text-slate-600 text-sm">Tier &amp; Terms</th>
              <th className="p-4 font-medium text-slate-600 text-sm">Status</th>
              {canEdit && (
                <th className="p-4 font-medium text-slate-600 text-right text-sm">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {companies.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-slate-500 italic">
                  No active companies found.
                </td>
              </tr>
            ) : (
              companies.map((company) => {
                const { approvedBy, approvedAt, tierSetBy, tierSetAt } =
                  getApprovalDetails(company);
                const statusDetails = getStatusChangeDetails(company);
                const restrictedCount =
                  company.allowedVenueIds &&
                  company.allowedVenueIds.length > 0 &&
                  company.allowedVenueIds.length < venues.length
                    ? company.allowedVenueIds.length
                    : 'All';
                const teamCount = Number(company.teamCount ?? company.teamMembers?.length ?? 0);
                return (
                  <tr key={company.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4 font-medium text-slate-900">
                      <div className="flex items-center gap-2">
                        {company.name}
                        {company.staffNotes && (
                          <div className="group/note relative">
                            <MessageSquare className="w-3.5 h-3.5 text-amber-500" />
                            <div className="absolute left-0 bottom-full mb-2 w-48 p-2 bg-slate-900 text-white text-[10px] rounded shadow-xl opacity-0 group-hover/note:opacity-100 pointer-events-none transition-opacity z-50">
                              {company.staffNotes}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-1 text-[10px] text-slate-500">
                        <MapPin className="w-3 h-3" /> Locations:{' '}
                        {restrictedCount === 'All' ? 'All' : `${restrictedCount} Allowed`}
                      </div>
                    </td>
                    <td className="p-4 text-slate-600 text-sm">
                      <div className="flex flex-col">
                        <span className="font-medium">{company.contactPerson}</span>
                        <span className="text-slate-400">{company.email}</span>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600 text-sm">{company.address}</td>
                    <td className="p-4 overflow-visible">
                      <div className="relative group w-fit flex flex-col items-start gap-1">
                        <span
                          className={`cursor-help px-2 py-1 rounded text-[10px] font-bold uppercase ${
                            company.tier === 'gold'
                              ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                              : company.tier === 'silver'
                                ? 'bg-slate-100 text-slate-600 border border-slate-200'
                                : company.tier === 'platinum'
                                  ? 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                                  : company.tier === 'diamond'
                                    ? 'bg-cyan-100 text-cyan-800 border border-cyan-200'
                                    : 'bg-orange-50 text-orange-800 border border-orange-200'
                          }`}
                        >
                          {company.tier}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-slate-500 flex items-center gap-1">
                            {company.paymentPreference === 'card' ? (
                              <CreditCard className="w-3 h-3" />
                            ) : (
                              <FileText className="w-3 h-3" />
                            )}
                            {company.paymentPreference === 'card' ? 'Pay Now' : 'Invoice'}
                          </span>
                          {company.requirePo && (
                            <span className="text-[9px] bg-red-50 text-red-500 font-bold px-1 rounded border border-red-100">
                              PO REQ
                            </span>
                          )}
                        </div>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-4 py-3 bg-slate-900 text-white text-xs rounded-xl shadow-xl invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all z-50 pointer-events-none">
                          <div className="space-y-3 text-left">
                            <div>
                              <p className="text-slate-400 text-[10px] uppercase font-bold mb-0.5">
                                Approved By
                              </p>
                              <div className="font-medium text-white text-sm">{approvedBy}</div>
                              <div className="text-[10px] text-slate-500">{approvedAt}</div>
                            </div>
                            <div className="border-t border-slate-700 pt-2">
                              <p className="text-slate-400 text-[10px] uppercase font-bold mb-0.5">
                                Tier Set By
                              </p>
                              <div className="font-medium text-white text-sm">{tierSetBy}</div>
                              <div className="text-[10px] text-slate-500">{tierSetAt}</div>
                            </div>
                          </div>
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 overflow-visible">
                      <div className="relative group w-fit">
                        <span
                          className={`cursor-help px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                            company.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : company.status === 'on_hold'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {company.status.toUpperCase().replace('_', ' ')}
                        </span>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-3 py-2 bg-slate-900 text-white text-xs rounded-lg shadow-xl invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all z-50 pointer-events-none">
                          <div className="space-y-1">
                            <p>
                              <span className="text-slate-400">Changed by:</span>{' '}
                              <span className="font-medium text-white">{statusDetails.actor}</span>
                            </p>
                            <p>
                              <span className="text-slate-400">Date:</span>{' '}
                              <span className="font-medium text-white">{statusDetails.date}</span>
                            </p>
                          </div>
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                        </div>
                      </div>
                    </td>
                    {canEdit && (
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => onViewActivity(company)}
                            className="text-slate-600 p-2 hover:bg-slate-100 rounded transition-all"
                            title="View bookings and customers"
                          >
                            <Calendar className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onViewTeam(company)}
                            className="text-purple-600 p-2 hover:bg-purple-50 rounded transition-all flex items-center gap-1 text-xs font-bold"
                            title="View Team"
                          >
                            <Users className="w-4 h-4" />{' '}
                            {teamCount > 0 && <span>({teamCount})</span>}
                          </button>
                          <button
                            onClick={() => onEdit(company)}
                            className="text-teal-600 p-2 hover:bg-slate-100 rounded transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onDelete(company)}
                            disabled={isDeletingCompanyId === company.id}
                            className="text-red-600 p-2 hover:bg-red-50 rounded transition-colors disabled:opacity-40"
                            title="Delete Company"
                          >
                            {isDeletingCompanyId === company.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </>
  );
};
